import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import InfoContext from "pages/InfoContext";
import NavigationBar from "components/navigation-bar/NavigationBar";
import { Typography } from "core/typography/Typography";
import AlertPopup from "components/alert-popup/AlertPopup";
import ValidationModal from "components/modal/ValidationModal";
import { INFO_HEADER_NAVIGATION } from "constants/info-header";
import {
  DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES,
  DELIVERY_NOTE_MODULE_INTRO,
  DELIVERY_NOTE_MODULE_TIP,
  DELIVERY_NOTE_TEMPLATE_MESSAGES,
  DELIVERY_NOTE_FILTERS_DEFAULTS,
} from "constants/forms";
import {
  DELIVERY_NOTE_FILTERS_ENABLED,
  DELIVERY_NOTES_SECTIONS,
  INITIAL_MODAL_FEEDBACK,
  QUERY_STATUS,
} from "constants/constants";
import { useUserContext } from "api/user/UserProvider";
import { useDeliveryNoteTemplateContext } from "api/delivery-note/DeliveryNoteTemplateProvider";
import {
  filterTemplates,
  hasActiveDeliveryNoteFilters,
} from "utils/deliveryNoteHelper";
import DeliveryNoteFilters from "components/form/delivery-notes/DeliveryNoteFilters";
import DeliveryNoteTemplateList from "components/form/delivery-notes/DeliveryNoteTemplateList";
import DeliveryNoteTemplateForm from "components/form/delivery-notes/DeliveryNoteTemplateForm";
import DeliveryNoteReceivedTemplateList from "components/form/delivery-notes/DeliveryNoteReceivedTemplateList";
import DeliveryNotePublicTemplateList from "components/form/delivery-notes/DeliveryNotePublicTemplateList";
import DeliveryNoteTemplateShareModal from "components/form/delivery-notes/DeliveryNoteTemplateShareModal";
import DeliveryNoteGenerator from "components/form/delivery-notes/DeliveryNoteGenerator";
import DeliveryNoteSavedList from "components/form/delivery-notes/DeliveryNoteSavedList";
import useDeliveryNotes from "api/delivery-note/useDeliveryNotes";
import "./DeliveryNotes.scss";

const SECTIONS = INFO_HEADER_NAVIGATION.deliveryNotes;

const renderBold = (chunks) => <b>{chunks}</b>;

const DeliveryNotes = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const { userScope } = useUserContext();
  const {
    deliveryNoteTemplates,
    loadTemplatesStatus,
    getDeliveryNoteTemplates,
    deleteDeliveryNoteTemplate,
    isDeletePending,
    toggleDeliveryNoteTemplateFavorite,
    toggleDeliveryNoteTemplatePublic,
    searchShareColleagues,
    shareDeliveryNoteTemplate,
    receivedTemplates,
    loadReceivedStatus,
    isReceivedActionPending,
    getReceivedDeliveryNoteTemplates,
    importReceivedDeliveryNoteTemplate,
    dismissReceivedDeliveryNoteTemplate,
    publicTemplates,
    loadPublicStatus,
    isPublicActionPending,
    getPublicDeliveryNoteTemplates,
    importPublicDeliveryNoteTemplate,
  } = useDeliveryNoteTemplateContext();
  const {
    deliveryNotes,
    loadNotesStatus,
    isSaveNotePending,
    isDeleteNotePending,
    getDeliveryNotes,
    mintDeliveryNoteNumber,
    createDeliveryNote,
    updateDeliveryNote,
    deleteDeliveryNote,
  } = useDeliveryNotes();

  const [section, setSection] = useState(
    Object.values(DELIVERY_NOTES_SECTIONS).includes(tab)
      ? tab
      : DELIVERY_NOTES_SECTIONS.saved
  );
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const [editedTemplate, setEditedTemplate] = useState(null);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [sharedTemplate, setSharedTemplate] = useState(null);
  const [editedNote, setEditedNote] = useState(null);
  const [pendingNote, setPendingNote] = useState(null);
  const [confirmation, setConfirmation] = useState(INITIAL_MODAL_FEEDBACK);
  const confirmAction = useCallback(
    (next) => setConfirmation({ open: true, ...next }),
    []
  );
  const [templateFilters, setTemplateFilters] = useState(
    DELIVERY_NOTE_FILTERS_DEFAULTS
  );

  const filteredTemplates = useMemo(
    () => filterTemplates(deliveryNoteTemplates, templateFilters),
    [deliveryNoteTemplates, templateFilters]
  );
  const filteredReceivedTemplates = useMemo(
    () => filterTemplates(receivedTemplates, templateFilters),
    [receivedTemplates, templateFilters]
  );
  const filteredPublicTemplates = useMemo(
    () => filterTemplates(publicTemplates, templateFilters),
    [publicTemplates, templateFilters]
  );
  const areTemplateFiltersActive =
    hasActiveDeliveryNoteFilters(templateFilters);

  useEffect(() => {
    getDeliveryNoteTemplates();
  }, [getDeliveryNoteTemplates]);

  useEffect(() => {
    getReceivedDeliveryNoteTemplates();
  }, [getReceivedDeliveryNoteTemplates]);

  useEffect(() => {
    getPublicDeliveryNoteTemplates();
  }, [getPublicDeliveryNoteTemplates]);

  const isSavedSection = section === DELIVERY_NOTES_SECTIONS.saved;
  useEffect(() => {
    getDeliveryNotes();
  }, [getDeliveryNotes, isSavedSection]);

  const changeSection = useCallback((nextSection) => {
    setEditedTemplate(null);
    setEditedNote(null);
    setSection(nextSection);
  }, []);

  useEffect(() => {
    if (!pendingTemplate || section !== DELIVERY_NOTES_SECTIONS.create) return;
    setEditedTemplate(pendingTemplate);
    setPendingTemplate(null);
  }, [section, pendingTemplate]);

  useEffect(() => {
    if (!pendingNote || section !== DELIVERY_NOTES_SECTIONS.generate) return;
    setEditedNote(pendingNote);
    setPendingNote(null);
  }, [section, pendingNote]);

  const values = useMemo(
    () => ({
      sections: SECTIONS,
      sectionCounts: {
        [DELIVERY_NOTES_SECTIONS.saved]: deliveryNotes.length,
      },
      setSection: changeSection,
      activeSection: section,
      setCode,
      setStatus,
      confirmAction,
    }),
    [section, changeSection, deliveryNotes.length, confirmAction]
  );

  const goToSection = (nextSection) => {
    changeSection(nextSection);
    navigate(`/${userScope}/delivery-notes/${nextSection}`);
  };

  const goToTemplates = () => goToSection(DELIVERY_NOTES_SECTIONS.templates);

  const goToSavedNotes = () => goToSection(DELIVERY_NOTES_SECTIONS.saved);

  const afterTemplateSaved = () => {
    setTemplateFilters(DELIVERY_NOTE_FILTERS_DEFAULTS);
    goToTemplates();
  };

  const handleSaveStatus = (nextStatus, overrides) => {
    if (nextStatus === QUERY_STATUS.INITIAL) {
      setConfirmation(INITIAL_MODAL_FEEDBACK);
      return;
    }
    if (nextStatus === QUERY_STATUS.SUCCESS) getDeliveryNoteTemplates();
    confirmAction({
      status: nextStatus,
      messages: {
        ...DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES[
          formTemplate?.id ? "templateUpdate" : "templateCreate"
        ],
        ...overrides,
      },
      onClose:
        nextStatus === QUERY_STATUS.SUCCESS ? afterTemplateSaved : undefined,
    });
  };

  const closeConfirmation = () => {
    const done = confirmation.onClose;
    setConfirmation(INITIAL_MODAL_FEEDBACK);
    done?.();
  };

  const editTemplate = (template) => {
    setPendingTemplate(template);
    setSection(DELIVERY_NOTES_SECTIONS.create);
    navigate(`/${userScope}/delivery-notes/${DELIVERY_NOTES_SECTIONS.create}`);
  };

  const editNote = (note) => {
    setPendingNote(note);
    setSection(DELIVERY_NOTES_SECTIONS.generate);
    navigate(
      `/${userScope}/delivery-notes/${DELIVERY_NOTES_SECTIONS.generate}`
    );
  };

  const formTemplate = pendingTemplate || editedTemplate;
  const formNote = pendingNote || editedNote;

  const renderTemplatesSection = () => (
    <div className="delivery-note-templates-section">
      <Typography
        className="title-3 violet text-uppercase"
        type="h1"
        label={<FormattedMessage {...DELIVERY_NOTE_TEMPLATE_MESSAGES.title} />}
      />
      <Typography
        className="title-5 light-slate-gray"
        type="p"
        label={
          <FormattedMessage {...DELIVERY_NOTE_TEMPLATE_MESSAGES.subtitle} />
        }
      />

      {DELIVERY_NOTE_FILTERS_ENABLED && (
        <DeliveryNoteFilters
          filters={templateFilters}
          setFilters={setTemplateFilters}
          hasActiveFilters={areTemplateFiltersActive}
        />
      )}
      <DeliveryNoteTemplateList
        templates={filteredTemplates}
        hasActiveFilters={areTemplateFiltersActive}
        filters={templateFilters}
        loadStatus={loadTemplatesStatus}
        onEdit={editTemplate}
        onToggleFavorite={toggleDeliveryNoteTemplateFavorite}
        onTogglePublic={toggleDeliveryNoteTemplatePublic}
        onShare={setSharedTemplate}
        onDelete={deleteDeliveryNoteTemplate}
        isDeleting={isDeletePending}
      />
      <DeliveryNoteReceivedTemplateList
        receivedTemplates={filteredReceivedTemplates}
        hasActiveFilters={areTemplateFiltersActive}
        loadStatus={loadReceivedStatus}
        isBusy={isReceivedActionPending}
        onImport={importReceivedDeliveryNoteTemplate}
        onDismiss={dismissReceivedDeliveryNoteTemplate}
      />
      <DeliveryNotePublicTemplateList
        publicTemplates={filteredPublicTemplates}
        hasActiveFilters={areTemplateFiltersActive}
        loadStatus={loadPublicStatus}
        isBusy={isPublicActionPending}
        onImport={importPublicDeliveryNoteTemplate}
      />
      <DeliveryNoteTemplateShareModal
        key={sharedTemplate?.id}
        template={sharedTemplate}
        onSearchColleagues={searchShareColleagues}
        onShare={shareDeliveryNoteTemplate}
        onClose={() => setSharedTemplate(null)}
      />
    </div>
  );

  const renderSection = () => {
    switch (section) {
      case DELIVERY_NOTES_SECTIONS.create:
        return (
          <DeliveryNoteTemplateForm
            key={formTemplate?.id || "new"}
            onCancel={goToTemplates}
            onSaveStatus={handleSaveStatus}
            template={formTemplate}
          />
        );
      case DELIVERY_NOTES_SECTIONS.generate:
        return (
          <DeliveryNoteGenerator
            key={formNote?.id || "new"}
            templates={deliveryNoteTemplates}
            loadTemplatesStatus={loadTemplatesStatus}
            isSaveNotePending={isSaveNotePending}
            mintDeliveryNoteNumber={mintDeliveryNoteNumber}
            createDeliveryNote={createDeliveryNote}
            note={formNote}
            updateDeliveryNote={updateDeliveryNote}
            onEditionDone={goToSavedNotes}
          />
        );
      case DELIVERY_NOTES_SECTIONS.saved:
        return (
          <DeliveryNoteSavedList
            deliveryNotes={deliveryNotes}
            loadStatus={loadNotesStatus}
            onSort={getDeliveryNotes}
            onEdit={editNote}
            onDelete={deleteDeliveryNote}
            isDeleting={isDeleteNotePending}
          />
        );
      default:
        return renderTemplatesSection();
    }
  };

  return (
    <InfoContext.Provider value={values}>
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <ValidationModal
        open={confirmation.open}
        status={confirmation.status}
        onClose={closeConfirmation}
        messages={confirmation.messages}
      />
      <div className="delivery-notes-page">
        <Typography
          className="delivery-notes-page__intro"
          type="p"
          label={
            <>
              <FormattedMessage {...DELIVERY_NOTE_MODULE_INTRO} />
              <br />
              <br />
              <FormattedMessage
                {...DELIVERY_NOTE_MODULE_TIP}
                values={{ b: renderBold }}
              />
            </>
          }
        />
        <NavigationBar />
        {renderSection()}
      </div>
    </InfoContext.Provider>
  );
};

export default DeliveryNotes;
